'use client'

import { useEffect, useMemo, useState } from 'react'

import { Checkbox } from '../checkbox'

import cls from './group.module.scss'

type GroupProps = {
	onChange?: (value: (string | number)[]) => void
	isDisabled?: boolean
	name?: string
	items: {
		value: string | number
		checked: boolean
		disabled?: boolean
		label?: string
		isHide?: boolean
	}[]
}

export const Group = (props: GroupProps) => {
	const { name, items, isDisabled, onChange } = props
	const [values, setValues] = useState<(string | number)[]>([])
	const checkedValuesSet = useMemo(() => new Set(values), [values])
	const hasLocalCheckedValues = values.length > 0

	const sortedItems = useMemo(
		() =>
			items
				.map((item, index) => ({
					...item,
					index
				}))
				.sort((a, b) => {
					const firstIsChecked = hasLocalCheckedValues
						? Number(checkedValuesSet.has(a.value))
						: Number(Boolean(a.checked))
					const secondIsChecked = hasLocalCheckedValues
						? Number(checkedValuesSet.has(b.value))
						: Number(Boolean(b.checked))

					if (firstIsChecked !== secondIsChecked) {
						return secondIsChecked - firstIsChecked
					}

					return a.index - b.index
				}),
		[items, checkedValuesSet, hasLocalCheckedValues]
	)

	useEffect(() => {
		const checkedValues = items.filter(({ checked }) => !!checked).map(({ value }) => value)

		setValues(checkedValues)
	}, [items])

	const onChangeHandler = (value: string | number, checked: boolean) => {
		let newValues = [...values]
		if (checked) {
			newValues.push(value)
		} else {
			newValues = newValues.filter(activeValue => activeValue !== value)
		}

		setValues(newValues)

		onChange?.(newValues)
	}

	return (
		<div className={cls.root}>
			{sortedItems.map(({ disabled, value, label, isHide }) => {
				const isChecked = checkedValuesSet.has(value)

				return (
					<div key={value} style={isHide ? { display: 'none' } : {}}>
						<Checkbox
							value={value}
							checked={isChecked}
							label={label}
							name={name}
							onClick={() => onChangeHandler(value, !isChecked)}
							disabled={isDisabled || disabled}
						/>
					</div>
				)
			})}
		</div>
	)
}
